import { Suspense, lazy } from 'react'
import { Loader2, UserCog } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import PopupCloseButton from 'components/PopupCloseButton'
import { EmailPreferencesSection } from 'features/Notifications/components/EmailPreferencesSection'

/**
 * Loaded on demand.
 *
 * The form pulls in the image uploader and the users service, all
 * behind a menu item that most sessions never open. On a phone that is worth
 * not shipping up front.
 *
 * It also keeps `components/controls/useForm` out of the static import graph.
 * That file is a `.js` containing JSX, which CRA's babel handles and vite's
 * esbuild does not, so a static import breaks any test that mounts the field
 * shell.
 */
const UserProfileForm = lazy(() => import('components/UserProfileForm/UserProfileForm'))

export interface UserProfilePopupProps {
	open: boolean
	onClose: () => void
}

/**
 * The user's profile, without leaving where they are.
 *
 * The profile used to be reachable only as a full screen wrapped in the admin
 * sidebar and header. From the field app that is a one-way door: opening it
 * ejects a user into desktop chrome they deliberately left, with the field app
 * a navigation away.
 *
 * `compact` stacks the fields and shrinks the avatar uploader. The form is laid
 * out in rows of three for a 1080px card, which is unusable in a dialog on a
 * phone.
 *
 * It does NOT close itself on save. Saving a profile is not finishing with it:
 * somebody correcting their phone number may well change their title next, and
 * the form already confirms the save on its own.
 */
export function UserProfilePopup({ open, onClose }: UserProfilePopupProps) {
	if (!open) return null

	return (
		<Dialog open onClose={onClose}>
			<DialogContent hideClose className="max-w-lg">
				<DialogHeader className="px-5 pt-5 pb-3">
					<PopupCloseButton onClose={onClose} className="absolute top-3 right-3 z-10" />
					<div className="flex items-center gap-3 pr-14">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0080fe]/10 text-[#0080fe]">
							<UserCog className="h-5 w-5" />
						</div>
						<div>
							<DialogTitle>User Profile</DialogTitle>
							<DialogDescription>Your details, photo and password</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				{/* The dialog caps at 90vh; the form is taller than a phone. */}
				<div className="max-h-[70svh] overflow-y-auto px-5 pb-5">
					<Suspense
						fallback={
							<p className="flex items-center gap-2 py-8 text-sm text-gray-500">
								<Loader2 className="h-4 w-4 animate-spin" />
								Loading your profile…
							</p>
						}
					>
						<UserProfileForm compact />
					</Suspense>

					{/* Partners on a phone need the email switches too (MYD-543). */}
					<EmailPreferencesSection className="mt-6 shadow-none" />
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default UserProfilePopup
